/**
 * The frame every screen sits in: mode edge, sidebar, header, content, footer.
 *
 * `UI-Design-Spec.md#navigation` fixes the layout and two of its rules are safety
 * requirements rather than preferences:
 *
 * > The kill switch is present on every screen, in the same place, at all times — this is
 * > a safety requirement, not a layout preference — and it never moves when the mode pill
 * > changes width.
 *
 * Both are properties of *this* component, which is why every screen renders inside it and
 * no screen renders its own header. `AppHeader` owns the ordering; the shell's job is to
 * make sure there is no way to be on a screen and not inside it.
 *
 * The amber mode edge is the second: `ModeEdge` draws the 2px top rule whenever real money
 * is on screen. Amber appears in exactly three places in this product and this is one of
 * them, so it is drawn by the design system's own component rather than by a border here.
 *
 * ## What is still fixed, and what stopped being
 *
 * `mode` is `paper`, because a shell that draws the real-money edge before any real money
 * exists is the one mistake this frame must not make.
 *
 * 🔴 **The footer's numbers were fixed too, and that outlived its premise — F-D80-16.** At task 29
 * there was genuinely no engine, no deployment and no book, so a constant `—` was honest. By
 * 2026-08-06 the engine was evaluating 168 candidates an hour and the rail still read
 * `ENGINE not running` and `SHADOW 0`. **A constant that is true when written has no way to become
 * false loudly** — it just stops matching, on the one element carrying `role="status"`. They come
 * from `GET /system/status` now; see `StatusRail`.
 */

import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';

import { api } from '../api/client';
import type { SystemStatusResponse } from '../api/generated';
import { useAuth } from '../auth/AuthProvider';
import { Button } from '../design/components/controls/Button.jsx';
import { Icon } from '../design/components/icon/Icon.jsx';
import { ModeEdge } from '../design/components/chrome/ModeMarker.jsx';
import { AppSidebar } from './AppSidebar';
import { AppTopBar } from './AppTopBar';
import { readTheme, writeTheme } from '../ui/preferences';
import { setTimestampMode, useTimestampMode } from '../ui/timestamps';
import {
  ACCELERATORS,
  DEFAULT_SCREEN,
  STOCK_SCREENS,
  pathFor,
  screenFor,
  titleFor,
} from './routes';

type Theme = 'dark' | 'light';

/** Below this the sidebar becomes the 56px icon rail. */
const COLLAPSE_BELOW_PX = 1280;

/**
 * At or below this the sidebar is an off-canvas drawer instead, and is **never** the icon rail.
 *
 * 🔴 **The two thresholds have to be read together, and reading them apart shipped a defect.**
 * `collapsed` was a bare `width < 1280`, so a 430px phone satisfied it and the drawer opened in
 * icon-only mode: fourteen unlabelled glyphs and a lone brand mark, which is exactly the memory
 * test the drawer was built to replace. The rail is a *desktop* space-saver; a phone has the
 * whole viewport to give the drawer, so below this width there is nothing to save.
 *
 * Must match the `max-width: 720px` media query in `app.css`.
 */
const DRAWER_AT_OR_BELOW_PX = 720;

function railFor(width: number): boolean {
  return width < COLLAPSE_BELOW_PX && width > DRAWER_AT_OR_BELOW_PX;
}

export function AppShell(): React.JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const { account, signOut } = useAuth();
  const active = screenFor(location.pathname) ?? DEFAULT_SCREEN;

  // Seeded from storage rather than hardcoded: the choice used to be forgotten on every reload,
  // which made a once-a-year preference something the operator re-made every morning.
  const [theme, setTheme] = useState<Theme>(() => readTheme());
  // Read from the store rather than held here: the header is one reader of this and every
  // timestamp in every screen is another, and those screens arrive through `<Outlet />`
  // where no prop from this component can reach them. See `ui/timestamps.ts`.
  const timestampMode = useTimestampMode();
  const [collapsed, setCollapsed] = useState(() => railFor(window.innerWidth));
  // 🔴 **The phone's menu is a drawer, and it is separate state from `collapsed` on purpose.**
  // `collapsed` is the desktop rail, driven by a breakpoint; `menuOpen` is a phone gesture the
  // operator performs. Conflating them meant the rail's breakpoint could silently open or close a
  // drawer nobody touched.
  const [menuOpen, setMenuOpen] = useState(false);

  /**
   * 🔴 **No mode edge anywhere in the Stocks section, and the absence *is* the signal.**
   *
   * `03-Architecture/Stocks/API-and-UI.md` works the argument through and it is a safety
   * requirement rather than a layout preference. The product's mode vocabulary has exactly two
   * states, and the Stocks section is neither of them:
   *
   * - **Amber would say *this system can act with real money here*.** It cannot — there is no
   *   order endpoint and no code path that could serve one. Amber must keep meaning exactly one
   *   thing, in exactly three places.
   * - **Blue would say *this money is fictional*.** Trading 212 holdings are real money the
   *   operator owns. Conflating them with paper is precisely the misread the two-mode vocabulary
   *   exists to prevent.
   *
   * The edge means *this system can act*, so its absence here is accurate rather than an
   * omission. What replaces it is a third marker in **geometry**: a dotted `BROKER · READ-ONLY`
   * pill against amber's solid and paper's dashed, in neutral ink.
   */
  const stocks = STOCK_SCREENS.has(active);

  // The tokens read `data-theme` and `data-density` off the root element — see
  // `design/tokens/density.css`. Setting React state alone would change nothing visible.
  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    // Persisted here rather than in the click handler, so the stored value follows the *applied*
    // one. A write beside `setTheme` would drift the moment anything else set the theme.
    writeTheme(theme);
  }, [theme]);
  // ⚠ **Pinned, not stateful.** The density control was removed at the operator's instruction,
  // but the vendored tokens still read `data-density` off the root and resolve to nothing without
  // it — `--row-h`, `--cell-pad` and `--cell-num-size` all come from `tokens/density.css`. So the
  // attribute is still set, once, to the step the product actually wants. The two tables that
  // care override it per table; nothing else in the product changes size.
  useEffect(() => {
    document.documentElement.dataset.density = 'comfortable';
  }, []);

  useEffect(() => {
    const onResize = () => setCollapsed(railFor(window.innerWidth));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // `g` then a letter, per UI-Design-Spec. Ignored while a field has focus, because an
  // operator typing a strategy name into a textarea must not be navigated away mid-word.
  useEffect(() => {
    let armed = false;
    const onKey = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (target && /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName)) return;
      if (target?.isContentEditable) return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (!armed) {
        armed = event.key === 'g';
        return;
      }
      armed = false;
      const destination = ACCELERATORS[event.key];
      if (destination) {
        event.preventDefault();
        navigate(pathFor(destination));
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [navigate]);

  // ⚠ **Navigating closes the drawer**, always. A drawer that stays open over the screen it just
  // navigated to hides the thing the operator asked for, and on a phone it hides all of it.
  const onNavigate = useCallback(
    (id: string) => {
      setMenuOpen(false);
      navigate(pathFor(id));
    },
    [navigate],
  );

  // Escape closes it, and the route changing closes it — the second covers a back-button or a
  // deep link arriving while the drawer is open.
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMenuOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [menuOpen]);

  return (
    <div className="jmt-shell">
      <ModeEdge mode={stocks ? null : 'paper'} />
      <div className="jmt-shell__body" data-menu={menuOpen ? 'open' : undefined}>
        {/* The scrim. Only rendered when the drawer is open, so it cannot swallow a tap on the
            desktop layout where the sidebar is not a drawer at all. */}
        {menuOpen ? (
          <button
            type="button"
            className="jmt-shell__scrim"
            aria-label="Close the menu"
            onClick={() => setMenuOpen(false)}
          />
        ) : null}
        <AppSidebar
          active={active}
          onNavigate={onNavigate}
          collapsed={collapsed}
          onCollapse={() => setCollapsed(!collapsed)}
          menuOpen={menuOpen}
          onCloseMenu={() => setMenuOpen(false)}
        />
        <div className="jmt-shell__column">
          <AppTopBar
            menuOpen={menuOpen}
            onMenu={() => setMenuOpen(!menuOpen)}
            title={titleFor(active)}
            mode={stocks ? null : 'paper'}
            marker={stocks ? 'BROKER · READ-ONLY' : undefined}
            markerShort={stocks ? 'BROKER' : undefined}
            markerTitle="Brokerage holdings are real money this system cannot touch"
            timestampMode={timestampMode}
            onTimestampMode={() => setTimestampMode(timestampMode === 'utc' ? 'local' : 'utc')}
            theme={theme}
            onTheme={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
            onKill={() => {
              // Task 29 has nothing to stop. Wired to the engine in M2; until then it
              // must not look armed, so it says what it is rather than doing nothing.
              window.alert('The kill switch is not wired up yet — there is no engine to stop.');
            }}
          />
          <main className="jmt-shell__main">
            <Outlet />
          </main>
          <StatusRail account={account?.email ?? null} onSignOut={() => void signOut()} />
        </div>
      </div>
    </div>
  );
}

/**
 * How often the rail re-asks. Matches the services' own heartbeat interval — they write a beat
 * every 15 s, so a faster poll cannot learn anything sooner and only costs a request.
 */
const STATUS_POLL_MS = 15_000;

/**
 * The `Data` field's word, and it says *how* rather than only *whether*.
 *
 * ⚠ **`—` when nothing is stored**, because "no bars at all" and "bars that are behind" are
 * different situations and only the second is a fault to chase. The endpoint distinguishes them
 * with a `null` and this preserves it rather than collapsing both to "stale".
 */
function describeData(status: SystemStatusResponse): string {
  if (status.data_age_s === null || status.data_age_s === undefined) return '—';
  if (status.data_fresh) return 'current';
  const minutes = Math.round(status.data_age_s / 60);
  return minutes >= 60 ? `${Math.round(minutes / 60)}h behind` : `${minutes}m behind`;
}

/**
 * The annunciator rail: fixed fields, fixed order, one lamp each, on every screen.
 *
 * 🔴 **This replaces `FooterStatusBar` plus the orphaned account strip above it** — three
 * stacked bars at the foot of every screen, two of which existed to right-align two items.
 *
 * ⚠ **It is deliberately not a `FooterStatusBar` with new CSS.** That component draws the
 * whole strip in `--loss` tint when the engine is down, and H10 makes *stopped* the normal
 * state: the engine halts on stale data, an unknown order state or a failed reconciliation,
 * so a rail that reads as an error would read as an error most mornings and be tuned out by
 * the second week. Here the **lamp** carries state and the label never changes colour, which
 * is how a flight-mode annunciator distinguishes *this is off* from *this is wrong*.
 *
 * ✅ **State is never colour alone.** `on` is a filled lamp, `caution` a filled lamp with a
 * ring, `off` a hollow outline — three shapes, so the rail survives a colour-blind reader and
 * a monochrome screenshot. Every value also states itself in words.
 *
 * 🔴 **The fields are readings now — F-D80-16.** They were a hardcoded array, and the docstring
 * that used to sit here explained why: at task 29 there was no engine, no deployment and no book,
 * so a constant `—` was the honest mark. **It stopped being honest and nothing noticed.** By
 * 2026-08-06 the engine was evaluating 168 candidates an hour and this rail still read
 * `ENGINE not running` and `SHADOW 0` — on an element carrying `role="status"`, which is the one
 * place a false reading is worst. The operator saw it and reasonably believed the fleet was down.
 *
 * ⚠ **The lesson is about placeholders that outlive their premise.** A constant that is true when
 * written has no way to become false loudly; it just quietly stops matching. `GET /system/status`
 * is now the source, and the only field still hardcoded is the one with no source at all.
 *
 * ✅ **`Core` is `—` on purpose and is not a bug.** D12's capital books are not built, so there is
 * no core equity; `PRODUCT.md` says an uncomputable metric renders as an em dash, never as `0`.
 * The endpoint returns `null` for it deliberately and this renders that faithfully.
 */
export function StatusRail({
  account,
  onSignOut,
}: {
  account: string | null;
  onSignOut: () => void;
}): React.JSX.Element {
  // ⚠ **Polled, not pushed.** The live socket carries trading events; service liveness is a
  // question about processes that may include the publisher itself, so a rail fed by the socket
  // would go quiet exactly when it had something to say. `STATUS_POLL_MS` matches the services'
  // own heartbeat interval — asking faster cannot learn anything sooner.
  const status = useQuery({
    queryKey: ['system-status'],
    queryFn: () => api.system.systemStatus(),
    refetchInterval: STATUS_POLL_MS,
    refetchIntervalInBackground: false,
  });

  // 🔴 **Each field carries an icon as well as its word, and the phone shows the icon.**
  // The operator asked for a smaller bar. Acronyms were the other option and were refused for the
  // reason `CMF · CMP · DNS` was: an abbreviation of a word that appears nowhere is a thing
  // somebody has to come and ask about. A mark is not an abbreviation — it is a second channel,
  // and the full word travels with it in `title` and the accessible name, so nothing is lost that
  // a hover or a screen reader cannot recover.
  //
  // ⚠ **Before the first answer arrives every field is `—` with an `off` lamp**, and that is the
  // one state this rail must get right: *unknown* is not *stopped*. Rendering `not running` while
  // the request is in flight is the defect this replaced, arriving a second at a time.
  const data = status.data;
  const fields: ReadonlyArray<{ label: string; value: string; lamp: string; icon: string }> = [
    {
      label: 'Engine',
      value: data ? (data.engine.running ? 'running' : 'not running') : '—',
      // `warn`, not `off`: a stopped engine mid-cohort is a thing to act on, while not yet
      // knowing is not. H10 makes *stopped* ordinary, so it is never drawn as an error — the
      // ring is what separates "this is off" from "this is wrong".
      lamp: data ? (data.engine.running ? 'on' : 'warn') : 'off',
      icon: 'cpu',
    },
    {
      label: 'Data',
      value: data ? describeData(data) : '—',
      lamp: data ? (data.data_fresh === null ? 'off' : data.data_fresh ? 'on' : 'caution') : 'off',
      icon: 'activity',
    },
    {
      label: 'Live',
      value: data ? String(data.live_deployments) : '—',
      // Amber is reserved: `--live` means real money is at risk and appears in three places.
      // A non-zero live count is one of them, and zero is not a caution — it is the fleet's
      // normal state for the whole of M3′.
      lamp: data && data.live_deployments > 0 ? 'caution' : 'off',
      icon: 'radio',
    },
    {
      label: 'Shadow',
      value: data ? String(data.shadow_deployments) : '—',
      lamp: data && data.shadow_deployments > 0 ? 'on' : 'off',
      icon: 'layers',
    },
    {
      // 🔴 Still a constant, and the only honest one: there is no books table to read.
      label: 'Core',
      value: data?.core_equity ?? '—',
      lamp: 'off',
      icon: 'pie-chart',
    },
  ];
  return (
    <div className="jmt-rail" role="status" aria-label="System status">
      {fields.map((field) => (
        <div className="jmt-rail__field" key={field.label} title={`${field.label}: ${field.value}`}>
          <span aria-hidden="true" className="jmt-rail__lamp" data-lamp={field.lamp} />
          <span aria-hidden="true" className="jmt-rail__icon">
            <Icon name={field.icon} size={13} />
          </span>
          <span className="jmt-rail__label">{field.label}</span>
          <span className="jmt-rail__value">
            <span className="jmt-sr-only">{field.label}: </span>
            {field.value}
          </span>
        </div>
      ))}
      <span className="jmt-rail__spacer" />
      <div className="jmt-rail__account">
        <span className="jmt-rail__email">{account ?? '—'}</span>
        <Button variant="link" density="dense" onClick={onSignOut}>
          Sign out
        </Button>
      </div>
    </div>
  );
}
